import { Switch } from "antd"
import Loader from "components/Loader/loader"
import React, { useEffect, useState } from "react"
import { FaSave, FaPlus, FaTrash } from "react-icons/fa"
import { MdCancel } from "react-icons/md"
import { RiCoupon3Line } from "react-icons/ri"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Label,
  FormFeedback,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const SheetModal = ({ open, close, vendorData }) => {
  const [loading, setLoading] = useState(false)
  const [promoCode, setPromoCode] = useState("")
  const [markup, setMarkup] = useState("")
  const [promoErrors, setPromoErrors] = useState({})
  const [promotions, setPromotions] = useState([])
  const [promotionCodeList, setPromotionCodeList] = useState([])

  const validate = () => {
    const errors = {}
    if (!promoCode.trim()) errors.promoCode = "Promotion code is required"
    if (!markup.toString().trim()) errors.markup = "Markup is required"
    else if (isNaN(markup) || Number(markup) < 0)
      errors.markup = "Enter a valid markup value"
    setPromoErrors(errors)
    return Object.keys(errors).length === 0
  }

  useEffect(() => {
    fetchPromotionCodes()
    if (vendorData) {
      getPromotions()
    }
  }, [vendorData])

  async function fetchPromotionCodes() {
    const APIdata = await fetchData(() => {}, "promotion/code")
    if (APIdata?.success) setPromotionCodeList(APIdata?.data || [])
  }

  async function getPromotions() {
    const APIdata = await fetchData(
      setLoading,
      `vendor/markup/${vendorData?.id}`
    )
    if (APIdata?.success) {
      setPromotions(APIdata?.data)
    }
  }

  const handleAdd = async () => {
    if (!validate()) return
    const APIdata = await FetchPostData(setLoading, "vendor/markup", {
      vendorId: vendorData?.id,
      promotionCode: promoCode,
      markup,
    })
    if (APIdata?.data?.success) {
      Swal.fire("Promotion Code Saved!", APIdata?.data?.message, "success")
      getPromotions()
      setPromoCode("")
      setMarkup("")
    } else {
      Swal.fire("Error", APIdata?.response?.data?.message, "error")
    }
  }

  const handleToggleDefault = async (id, checked) => {
    const APIdata = await FetchPutData(
      setLoading,
      "vendor/markup/mark/as/default",
      id,
      { isDefault: checked }
    )
    if (APIdata?.data?.success) {
      getPromotions()
    } else {
      Swal.fire("Error", APIdata?.response?.data?.message, "error")
    }
  }

  const handleDelete = id => {
    Swal.fire({
      title: "Remove Promotion?",
      text: "Are you sure you want to remove this promotion code?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "vendor/markup", id)
        if (APIdata?.data?.success) {
          Swal.fire(
            "Promotion Code Deleted!",
            APIdata?.data?.message,
            "success"
          )
          getPromotions()
        } else {
          Swal.fire("Error", APIdata?.response?.data?.message, "error")
        }
      }
    })
  }

  return (
    <Modal isOpen={open} toggle={close} size="lg" centered>
      <ModalHeader toggle={close}>
        <div className="d-flex align-items-center gap-2">
          <RiCoupon3Line size={20} />
          <span> Promotion Codes</span>
        </div>
      </ModalHeader>

      <ModalBody>
        {loading && <Loader />}

        {/* Add Form */}
        <div
          style={{
            background: "#f8f9fc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <p className="fw-semibold mb-3" style={{ color: "#1c6aaa" }}>
            <FaPlus size={11} className="me-1" />
            Add New Promotion
          </p>

          <div className="row g-3 align-items-end">
            <div className="col-md-5">
              <Label>
                Promotion Code <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={promoCode}
                invalid={!!promoErrors.promoCode}
                onChange={e => {
                  const selected = promotionCodeList.find(pc => pc.promotionCode === e.target.value)
                  setPromoCode(e.target.value)
                  setMarkup(selected ? selected.markup : "")
                  setPromoErrors(prev => ({ ...prev, promoCode: "", markup: "" }))
                }}
              >
                <option value="">— Select Promotion Code —</option>
                {promotionCodeList.map(pc => (
                  <option key={pc.id} value={pc.promotionCode}>
                    {pc.promotionCode}
                  </option>
                ))}
              </Input>
              <FormFeedback>{promoErrors.promoCode}</FormFeedback>
            </div>

            <div className="col-md-5">
              <Label>
                Markup (%) <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                placeholder="e.g. 10"
                value={markup}
                min={0}
                invalid={!!promoErrors.markup}
                onChange={e => {
                  setMarkup(e.target.value)
                  setPromoErrors(prev => ({ ...prev, markup: "" }))
                }}
              />
              <FormFeedback>{promoErrors.markup}</FormFeedback>
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <Button
                className="btn-blue w-100"
                onClick={handleAdd}
                style={{ height: "38px" }}
              >
                <FaPlus size={11} className="me-1" /> Add
              </Button>
            </div>
          </div>
        </div>

        {/* List */}
        {promotions.length === 0 ? (
          <div
            className="text-center py-5"
            style={{
              color: "#aab",
              border: "1.5px dashed #dde4ed",
              borderRadius: "10px",
            }}
          >
            <RiCoupon3Line
              size={40}
              style={{ marginBottom: "10px", opacity: 0.35 }}
            />
            <p className="mb-0 fw-semibold" style={{ fontSize: "14px" }}>
              No promotion codes added yet
            </p>
            <p style={{ fontSize: "12px", marginTop: "4px" }}>
              Fill in the form above and click Add
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: "340px", overflowY: "auto" }}>
            <Table bordered responsive>
              <thead className="table-blue">
                <tr>
                  <th style={{ width: "5%" }}>#</th>
                  <th style={{ width: "40%" }}>Promotion Code</th>
                  <th style={{ width: "20%" }}>Markup (%)</th>
                  <th style={{ width: "20%", textAlign: "center" }}>Default</th>
                  <th style={{ width: "15%", textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {promotions?.reverse().map((promo, index) => (
                  <tr key={promo.id}>
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <span
                        style={{
                          background: "#e0eef8",
                          color: "#1c6aaa",
                          fontWeight: 700,
                          padding: "3px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {promo.promotionCode}
                      </span>
                    </td>
                    <td className="fw-semibold">{promo.markup}%</td>
                    <td className="text-center">
                      <Switch
                        checked={!!promo.isDefault}
                        onChange={checked =>
                          handleToggleDefault(promo.id, checked)
                        }
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => handleDelete(promo.id)}
                        style={{ padding: "3px 10px" }}
                      >
                        <FaTrash size={11} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default SheetModal
